import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../aws-exports';

/**
 * Calls of the boothBooking Lambda fields (getEventBoothMap, reserveBooth,
 * releaseBoothReservation, joinBoothWaitlist, leaveBoothWaitlist, and the admin fields
 * createBoothOption / releaseBoothOption).
 *
 * The Lambda identifies the caller by the `custom:userId` claim, which only the
 * Cognito ID token carries. Amplify v6 generateClient() sends the ACCESS token, so
 * these fields are called with a raw fetch and the ID token (same pattern as the
 * placeOrder call in src/pages-user/order/checkout/Checkout.jsx).
 *
 * All functions throw Error(message) when the response has GraphQL errors or no data.
 * Business outcomes of the actions (HELD, BOOTH_UNAVAILABLE, HOLD_LIMIT, ...) come back
 * as BoothActionResult objects with success/code - they do not throw.
 */

const ACTION_RESULT_FIELDS = /* GraphQL */ `
  success
  code
  message
  boothNumber
  status
  expiresAt
  reservationId
`;

// getEventBoothMap is declared on `type Mutation` (functions.graphql), so it MUST be sent as
// a mutation operation - AppSync rejects `query { getEventBoothMap }` with a validation error.
export const GET_EVENT_BOOTH_MAP = /* GraphQL */ `
  mutation GetEventBoothMap($input: BoothMapInput!) {
    getEventBoothMap(input: $input)
  }
`;

const actionMutation = (name) => /* GraphQL */ `
  mutation ${name[0].toUpperCase()}${name.slice(1)}($input: BoothActionInput!) {
    ${name}(input: $input) {
      ${ACTION_RESULT_FIELDS}
    }
  }
`;

async function getIdToken() {
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  if (!idToken) throw new Error('Not signed in');
  return idToken.toString();
}

async function callGraphql(query, variables, fieldName) {
  const idToken = await getIdToken();
  const response = await fetch(awsConfig.aws_appsync_graphqlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (error) {
    throw new Error(`${fieldName}: invalid response (HTTP ${response.status})`);
  }
  if (body && Array.isArray(body.errors) && body.errors.length > 0) {
    throw new Error(body.errors.map((e) => e && e.message).filter(Boolean).join('; ') || `${fieldName} failed`);
  }
  if (!response.ok) throw new Error(`${fieldName} failed (HTTP ${response.status})`);
  const value = body && body.data ? body.data[fieldName] : undefined;
  if (value === undefined || value === null) throw new Error(`${fieldName} returned no data`);
  return value;
}

/**
 * BoothActionInput of the action fields: only the declared fields are sent (callers pass whole map
 * booths). boothNumber (the physical booth; required for variant products) is sent trimmed and only
 * when it is non-empty.
 */
export function cleanActionInput({ eventId, eventProductId, companyId, boothNumber } = {}) {
  const input = { eventId, eventProductId, companyId };
  const number = boothNumber === null || boothNumber === undefined ? '' : String(boothNumber).trim();
  if (number) input.boothNumber = number;
  return input;
}

/** Booth map of an event (MAP CONTRACT object) for the given company. */
export async function fetchEventBoothMap({ eventId, companyId } = {}) {
  const input = { eventId };
  if (companyId) input.companyId = companyId;
  const raw = await callGraphql(GET_EVENT_BOOTH_MAP, { input }, 'getEventBoothMap');
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch (error) {
    throw new Error('getEventBoothMap returned invalid JSON');
  }
}

/** 30-minute hold on a free booth (code HELD on success). */
export function reserveBooth(input) {
  return callGraphql(actionMutation('reserveBooth'), { input: cleanActionInput(input) }, 'reserveBooth');
}

/** Releases the company's own hold (code RELEASED). */
export function releaseBoothReservation(input) {
  return callGraphql(actionMutation('releaseBoothReservation'), { input: cleanActionInput(input) }, 'releaseBoothReservation');
}

/** Waitlist entry for a booth held/reserved by another company (code WAITLISTED). */
export function joinBoothWaitlist(input) {
  return callGraphql(actionMutation('joinBoothWaitlist'), { input: cleanActionInput(input) }, 'joinBoothWaitlist');
}

/** Removes the company from the waitlist of a booth (code WAITLIST_LEFT). */
export function leaveBoothWaitlist(input) {
  return callGraphql(actionMutation('leaveBoothWaitlist'), { input: cleanActionInput(input) }, 'leaveBoothWaitlist');
}

/** Admin/Moderator only: option (status 'active', no end date) on one booth for a company; may replace a hold. */
export function createBoothOption(input) {
  return callGraphql(actionMutation('createBoothOption'), { input: cleanActionInput(input) }, 'createBoothOption');
}

/** Admin/Moderator only: removes the option (status 'active') on one booth. */
export function releaseBoothOption(input) {
  return callGraphql(actionMutation('releaseBoothOption'), { input: cleanActionInput(input) }, 'releaseBoothOption');
}
